import { makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { headerColor, linkGreen, textColor } from '../../components/Shared/Shared';

export const useLevelThreeStyles = makeStyles({
  container: {
    backgroundColor: tokens.colorNeutralForegroundInverted,
    zIndex: 2,
    minHeight: '100vh',
    minWidth: 0,
    boxSizing: 'border-box',
    ...shorthands.margin('24px'),
    ...shorthands.padding('20px'),
    '@media (max-width: 640px)': {
      ...shorthands.margin('12px'),
      ...shorthands.padding('8px'),
    },
  },
  operationsContainer: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'flex-start',
    '@media (max-width: 640px)': {
      flexWrap: 'wrap',
      rowGap: '8px',
    },
  },
  buttonLabel: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '0px',
    paddingRight: '40px',
    ':hover': { color: linkGreen },
  },
  buttonColor: {
    color: headerColor,
    ':hover': { color: linkGreen },
  },
  textContents: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    gridGap: '4px',
    marginBottom: '16px',
    overflowWrap: 'anywhere',
  },
  srOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    ...shorthands.padding('0px'),
    ...shorthands.margin('-1px'),
    ...shorthands.overflow('hidden'),
    ...shorthands.border('0px'),
  },
  text: {
    display: 'block',
    marginBottom: '16px',
  },
  title: {
    display: 'block',
    marginBottom: '24px',
    marginTop: '26px',
    fontSize: '22px',
    color: '#323130',
  },
  subtitle: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    marginTop: '0px',
  },
  subtitleColor: {
    color: tokens.colorNeutralForeground2,
    fontWeight: 500,
  },
  separatorHeader: {
    display: 'block',
    backgroundColor: '#E7F2DA',
    marginTop: '36px',
    marginBottom: '36px',
  },
  skillsSubtitleContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: '24px',
    paddingLeft: '32px',
    paddingRight: '32px',
  },
  tooltipStyle: {
    marginLeft: '16px',
    color: '#605E5C',
  },
  seeMoreButton: {
    color: linkGreen,
    ...shorthands.padding(0, '40px', 0, 0),
    display: 'flex',
    alignItems: 'flex-start',
    alignContent: 'flex-start',
    ':hover': { color: linkGreen, textDecorationLine: 'underline' },
    ':focus': {
      textDecorationLine: 'underline',
    },
  },
  skillsSubtitle: {
    marginLeft: '8px',
    display: 'block',
    color: '#595757',
  },
  skillsList: {
    columnCount: 3,
    paddingLeft: '0px',
    '@media (max-width: 640px)': {
      columnCount: 1,
    },
  },
  tabList: {
    marginTop: '24px',
    marginBottom: '24px',
    marginLeft: '-12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '@media (max-width: 640px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  roleTabList: {
    minWidth: 0,
    '@media (max-width: 640px)': {
      flexWrap: 'wrap',
    },
  },
  tab: {
    ':hover::after': { backgroundColor: linkGreen },
    '::after': { backgroundColor: linkGreen },
  },
  unSelectedTabName: {
    color: '#605e5c',
    fontSize: '14px',
  },
  section: {
    marginBottom: '32px',
    display: 'block',
    paddingLeft: '46px',
    paddingRight: '46px',
    '@media (max-width: 640px)': {
      paddingLeft: '0px',
      paddingRight: '0px',
    },
  },
  textContentsContainer: {
    display: 'grid',
    minWidth: 0,
    gridGap: '24px',
    gridTemplateColumns: 'repeat(2, 1fr)',
    '@media (max-width: 640px)': {
      gridTemplateColumns: '1fr',
    },
  },
  buttonIcon: {
    marginRight: '6px',
    display: 'inline-block',
    verticalAlign: 'middle',
    width: '12px',
    flexShrink: 0,
  },
  responsibilitiesContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    verticalAlign: 'flex-start',
    alignItems: 'flex-start',
    marginBottom: '24px',
    marginTop: '16px',
  },
  bulletImg: {
    height: '18px',
    width: '3px',
    marginRight: '16px',
    color: '#A19F9D', //black color
  },
  responsibilitiesTitle: {
    fontSize: '18px',
    fontWeight: 600,
    lineHeight: '24px',
    letterSpacing: '0em',
    textAlign: 'left',
  },
  responsibilityTitleContainer: {
    marginTop: '16px',
    marginBottom: '24px',
  },
  responsibilitiesSubtitle: {
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '20px',
    letterSpacing: '0em',
    textAlign: 'left',
  },
  truncatedText: {
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  dropdownStyle: {
    maxWidth: '200px',
    width: 'auto',
    borderBottomColor: tokens.colorNeutralStroke1,
    ':focus-visible': {
      outlineStyle: 'solid',
      outlineWidth: '2px',
      outlineColor: tokens.colorStrokeFocus2,
      outlineOffset: '2px',
    },
  },
  dropdownContainerStyle: {
    display: 'flex',
    minWidth: 0,
    justifyContent: 'center',
    alignItems: 'center',
    '@media (max-width: 640px)': {
      flexWrap: 'wrap',
      justifyContent: 'flex-start',
      rowGap: '8px',
    },
  },
  skillHeader: {
    color: headerColor,
  },
  skillDescription: {
    color: textColor,
  },
  capabilityHeader: {
    color: headerColor,
    fontSize: '18px',
    textAlign: 'left',
  },
  capabilityHeadingContainer: {
    margin: 0,
    font: 'inherit',
    color: 'inherit',
  },
  capabilityContainer: {
    display: 'flex',
    minWidth: 0,
    justifyContent: 'space-between',
    width: '100%',
    backgroundColor: 'transparent',
    ...shorthands.borderTop('0px'),
    ...shorthands.borderRight('0px'),
    ...shorthands.borderBottom('1px', 'solid', '#E1DFDD'),
    ...shorthands.borderLeft('0px'),
    ...shorthands.padding('0px'),
    color: 'inherit',
    font: 'inherit',
    textAlign: 'left',
    marginBottom: '24px',
    cursor: 'pointer',
  },
  capabilityDescription: {
    display: 'block',
    color: textColor,
    marginBottom: '24px',
    marginTop: '8px',
  },
  expandAll: {
    color: linkGreen,
    marginLeft: '16px',
    cursor: 'pointer',
    '@media (max-width: 640px)': {
      marginLeft: 0,
    },
  },
});
